/**
 * useNanoMetadataTableSync (Intelligence Studio)
 *
 * ✅ ENTERPRISE V2: Strictly Deterministic State Machine
 *
 * CRITICAL RULES:
 *   1. Status = "ready" ONLY if backend says isReady === true
 *   2. Health = NULL until processingProgress === 100 (NO speculation!)
 *   3. NO engineering jargon shown to users (no "chunks", "vectors")
 *   4. ALL components derive from SAME source of truth
 *
 * USER-FACING STAGES (replaces technical jargon):
 *   - "Uploading…" (uploading)
 *   - "Queued…" (queued)
 *   - "Analyzing structure…" (processing, early)
 *   - "Extracting text…" (processing, mid)
 *   - "Building intelligence…" (processing, late)
 *   - "Ready" (isReady === true)
 */

import { useCallback, useEffect } from 'react';

// ═══════════════════════════════════════════════════════════════════════════════
// USER-FACING STAGE DESCRIPTIONS (NO technical jargon!)
// ═══════════════════════════════════════════════════════════════════════════════
const PROCESSING_STAGES = {
  uploading: 'Uploading…',
  queued: 'Queued…',
  processing_early: 'Analyzing structure…',
  processing_mid: 'Extracting text…',
  processing_late: 'Building intelligence…',
  ready: 'Ready',
  error: 'Processing failed',
};

function getProcessingStage(status, progress) {
  if (status === 'error') return PROCESSING_STAGES.error;
  if (status === 'ready') return PROCESSING_STAGES.ready;
  if (status === 'uploading') return PROCESSING_STAGES.uploading;
  if (status === 'queued') return PROCESSING_STAGES.queued;

  // Processing stages based on progress
  if (progress === null || progress === undefined || progress < 20) {
    return PROCESSING_STAGES.processing_early;
  } else if (progress < 60) {
    return PROCESSING_STAGES.processing_mid;
  } else {
    return PROCESSING_STAGES.processing_late;
  }
}

// Helper for correct plurality
const plural = (count, singular) => {
  if (count === null || count === undefined) return singular;
  return count === 1 ? `1 ${singular}` : `${count} ${singular}s`;
};

// Map backend/internal error codes to user-friendly reasons (no jargon)
function humanizeProcessingError(codeOrMsg) {
  const raw = String(codeOrMsg || '').trim();
  const code = raw.toUpperCase();
  if (!code) return 'Processing failed';
  if (code === 'LOW_OCR_QUALITY') return 'Text extraction quality too low (Retry OCR recommended)';
  if (code === 'USER_DAILY_JOB_LIMIT') return 'Daily processing limit reached (try again tomorrow)';
  if (code === 'TENANT_DAILY_COST_LIMIT')
    return 'Organization daily limit reached (try again tomorrow)';
  if (code === 'FEATURE_DISABLED') return 'Processing disabled for this feature';
  if (code === 'REDIS_UNAVAILABLE' || code === 'REDIS_POLICY_UNAVAILABLE')
    return 'Processing temporarily unavailable';
  if (code === 'QUEUE_ENQUEUE_TIMEOUT' || code === 'QUEUED_UNAVAILABLE')
    return 'Processing temporarily unavailable';
  return raw;
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN HOOK
// ═══════════════════════════════════════════════════════════════════════════════
export default function useNanoMetadataTableSync({
  selectedNotebook,
  nanoMetadataRows,
  setNanoMetadataRowsDirectly,
}) {
  const formatBytes = useCallback(bytes => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // STRICTLY DETERMINISTIC FILE → ROW CONVERSION
  // ═══════════════════════════════════════════════════════════════════════════
  const convertFilesToSmartTableRows = useCallback(
    files => {
      if (!files || files.length === 0) return [];

      return files.map(file => {
        const fileName = file.name || file.originalName || 'Unknown';
        const extension = fileName.split('.').pop()?.toLowerCase() || '';
        const size = file.size || file.fileSize || 0;
        const stableFileId =
          file.fileId || file.id || `file_${fileName.replace(/[^a-zA-Z0-9]/g, '_')}`;

        // ═══════════════════════════════════════════════════════════════════════
        // DETERMINE FILE TYPE
        // ═══════════════════════════════════════════════════════════════════════
        let baseType = 'document';
        if (['pdf'].includes(extension)) baseType = 'pdf';
        else if (['doc', 'docx'].includes(extension)) baseType = 'word';
        else if (['xls', 'xlsx', 'csv'].includes(extension)) baseType = 'excel';
        else if (['ppt', 'pptx'].includes(extension)) baseType = 'powerpoint';
        else if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic'].includes(extension))
          baseType = 'image';
        else if (['txt', 'md', 'json', 'xml'].includes(extension)) baseType = 'text';

        // ═══════════════════════════════════════════════════════════════════════
        // EXTRACT BACKEND FLAGS (Source of Truth)
        // ═══════════════════════════════════════════════════════════════════════
        const lifecycle = file.lifecycleSnapshot || null;
        const rawStatus = String(lifecycle?.status || file.status || '').toLowerCase();
        const processingProgress =
          typeof lifecycle?.progress === 'number'
            ? lifecycle.progress
            : typeof file.processingProgress === 'number'
              ? file.processingProgress
              : null;

        // ═══════════════════════════════════════════════════════════════════════
        // ✅ ENTERPRISE V2: STRICTLY DETERMINISTIC isReady flag
        //
        // PRIORITY ORDER:
        // 1. file.isReady === true (new records with explicit flag)
        // 2. rawStatus === 'ready' && hasActualContent (legacy records without flag)
        // 3. ELSE → still processing
        //
        // This ensures backward compatibility with existing database records
        // while still being strictly deterministic.
        // ═══════════════════════════════════════════════════════════════════════
        const hasActualContent = (file.chunkCount || 0) > 0 || (file.embeddingCount || 0) > 0;
        const isReadyFlag =
          lifecycle?.isReady === true ||
          file.isReady === true ||
          (rawStatus === 'ready' && hasActualContent);
        const isComplete = isReadyFlag;

        // ═══════════════════════════════════════════════════════════════════════
        // STRICTLY DETERMINISTIC STATUS
        // Rule: ONLY "ready" if backend says isReady === true
        // ═══════════════════════════════════════════════════════════════════════
        let status;
        let statusReason;
        const errorCode = file.errorCode || file.errorReason || file.error || null;

        if (rawStatus === 'error' || rawStatus === 'failed') {
          status = 'error';
          statusReason = humanizeProcessingError(errorCode);
        } else if (rawStatus === 'duplicate') {
          status = 'duplicate';
          statusReason = 'File already exists';
        } else if (isReadyFlag) {
          // ONLY ready if backend explicitly says so
          status = 'ready';
          statusReason = 'Ready for AI analysis';
        } else if (rawStatus === 'uploading') {
          status = 'uploading';
          statusReason = 'Uploading file...';
        } else {
          // Everything else is processing (queued, processing, analyzing, or unknown)
          status = 'processing';
          statusReason = getProcessingStage('processing', processingProgress);
        }

        // ═══════════════════════════════════════════════════════════════════════
        // FILE METADATA (Always available)
        // ═══════════════════════════════════════════════════════════════════════
        const pageCount = file.pageCount || file.pages || file.numPages || null;
        const hasTextLayer = file.hasTextLayer ?? file.metadata?.hasTextLayer ?? null;
        const ocrRequired = baseType === 'image' || (baseType === 'pdf' && hasTextLayer === false);

        // ═══════════════════════════════════════════════════════════════════════
        // PROCESSING METRICS (Only meaningful after completion)
        // ═══════════════════════════════════════════════════════════════════════
        const chunkCount = Number(file.chunkCount || 0);
        const embeddingCount = Number(file.embeddingCount || file.vectorCount || 0);
        const indexCoveragePct = (() => {
          if (typeof file.indexCoveragePct === 'number') return Math.round(file.indexCoveragePct);
          const indexOk = file.vectorIndexReady !== false;
          const queryOk = file.embeddingQueryReady !== false;
          let pct = 0;
          if (chunkCount > 0) pct += 35;
          if (embeddingCount > 0) pct += 35;
          if (indexOk) pct += 20;
          if (queryOk) pct += 10;
          return pct > 0 ? Math.max(0, Math.min(100, pct)) : null;
        })();
        const textHealthPct = (() => {
          const v =
            (typeof file.textHealthPct === 'number' ? file.textHealthPct : null) ??
            (typeof file.qualityScore === 'number' ? file.qualityScore : null) ??
            null;
          return typeof v === 'number' ? Math.round(v) : null;
        })();

        // ═══════════════════════════════════════════════════════════════════════
        // HEALTH: STRICTLY NULL UNTIL COMPLETE
        // Rule: NO health score until processingProgress === 100
        // ═══════════════════════════════════════════════════════════════════════
        let healthScore = null;
        let healthTier = 'pending';
        let healthDiagnosis = [];

        if (status === 'error') {
          healthScore = 0;
          healthTier = 'critical';
          healthDiagnosis = [statusReason];
        } else if (isComplete) {
          // ONLY calculate health after processing is complete
          // ✅ Truth layer: treat Health as INDEX readiness (coverage) and keep Text Health separate
          const backendHealth = indexCoveragePct ?? file.health ?? file.ocrQuality?.health ?? null;
          const ocrConfidence = file.ocrConfidence ?? file.confidence ?? null;

          if (backendHealth !== null && backendHealth !== undefined) {
            healthScore = backendHealth;
          } else if (ocrConfidence !== null) {
            healthScore = ocrConfidence;
          } else if (chunkCount > 0) {
            // Estimate based on extraction quality
            const charCount = file.charCount || 0;
            if (pageCount && charCount > 0) {
              const charsPerPage = charCount / pageCount;
              healthScore =
                charsPerPage >= 1500
                  ? 95
                  : charsPerPage >= 1000
                    ? 85
                    : charsPerPage >= 500
                      ? 70
                      : 50;
            } else {
              healthScore = 80; // Default for processed files
            }
          }

          // Calculate tier
          if (healthScore !== null) {
            healthTier =
              healthScore >= 95
                ? 'excellent'
                : healthScore >= 85
                  ? 'good'
                  : healthScore >= 70
                    ? 'fair'
                    : healthScore >= 50
                      ? 'poor'
                      : 'critical';
          }
        }
        // If not complete and not error, healthScore stays NULL (shows "Pending")

        // ═══════════════════════════════════════════════════════════════════════
        // USER-FACING DESCRIPTIONS (NO technical jargon!)
        // ═══════════════════════════════════════════════════════════════════════
        const processingStage = getProcessingStage(status, processingProgress);

        // DNA description - user friendly, no "chunks" or "vectors"
        let dnaDescription;
        if (status === 'ready') {
          dnaDescription = pageCount ? `${plural(pageCount, 'Page')} indexed` : 'Document indexed';
        } else if (status === 'error') {
          dnaDescription = pageCount
            ? `${plural(pageCount, 'Page')} (metadata extracted)`
            : 'Metadata extracted';
        } else {
          dnaDescription = processingStage;
        }

        // Searchable pages - only show when complete
        let searchableDisplay = null;
        if (isComplete && pageCount) {
          const searchablePages =
            chunkCount > 0 ? Math.min(pageCount, Math.ceil(chunkCount * 1.5)) : 0;
          searchableDisplay = `${searchablePages} / ${pageCount} ${plural(pageCount, 'page').split(' ')[1]} searchable`;
        } else if (pageCount && status === 'processing') {
          searchableDisplay = `0 / ${pageCount} ${pageCount === 1 ? 'page' : 'pages'} searchable`;
        }

        // Insight text - user friendly
        let microInsight;
        const nanoMicroInsight =
          file.nanoMetadata?.details?.micro_insight ||
          file.nanoMetadata?.details?.microInsight ||
          file.nanoMetadata?.micro_insight ||
          file.nanoMetadata?.microInsight ||
          null;
        if (status === 'ready') {
          microInsight = `${baseType.charAt(0).toUpperCase() + baseType.slice(1)}: ${pageCount ? plural(pageCount, 'page') : 'Document'} ready for AI analysis.`;
        } else if (status === 'error') {
          // Phase-1 nano-metadata can succeed even if Phase-2 processing is blocked/failed.
          microInsight =
            nanoMicroInsight || `Could not process ${baseType} due to a policy or service limit.`;
        } else {
          microInsight = `${baseType.charAt(0).toUpperCase() + baseType.slice(1)}: ${processingStage}`;
        }

        // ═══════════════════════════════════════════════════════════════════════
        // BUILD ROW
        // ═══════════════════════════════════════════════════════════════════════
        return {
          file_id: stableFileId,
          filename: fileName,

          // ✅ DETERMINISTIC STATUS (from isReady flag)
          status,
          statusReason,
          errorCode,
          isLoading: status === 'uploading' || status === 'processing',
          hasError: status === 'error',
          isReady: status === 'ready',

          // ✅ PROCESSING PROGRESS
          processingProgress,
          processingStage,

          // ✅ FILE INFO
          basic_info: {
            name: fileName,
            extension,
            base_type: baseType,
            size_bytes: size,
            size_formatted: formatBytes(size),
          },

          // ✅ DOCUMENT STRUCTURE (user-facing)
          document_info: {
            pageCount,
            hasTextLayer,
            ocrRequired,
            pageDisplay: pageCount ? plural(pageCount, 'Page') : null,
          },

          // ✅ HEALTH (NULL until complete - NO speculation!)
          quick_health: {
            ai_readiness_score: healthScore,
            indexCoveragePct,
            textHealthPct,
            health_tier: healthTier,
            diagnosis: healthDiagnosis,
            is_pending: !isComplete && status !== 'error',
          },

          // ✅ USER-FACING DESCRIPTIONS (NO jargon!)
          user_display: {
            dna: dnaDescription,
            searchable: searchableDisplay,
            insight: microInsight,
            stage: processingStage,
            // ✅ FINAL UX CONTRACT: single user-facing trust badge (server-side)
            trust: file.userTrust || null,
          },

          // ✅ INTERNAL METRICS (hidden from user, for debugging only)
          _internal: {
            chunkCount,
            embeddingCount,
            isReadyFlag,
            rawStatus,
          },

          // Legacy compatibility
          details: {
            page_count: pageCount,
            micro_insight: microInsight,
          },
          processing_status: {
            ocr: ocrRequired ? (isComplete ? 'completed' : 'required') : 'not_needed',
          },
          context_type_guess: { type: 'Document', icon: '📄' },
        };
      });
    },
    [formatBytes]
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // SYNC EFFECT
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!selectedNotebook || !selectedNotebook.files || selectedNotebook.files.length === 0) {
      return;
    }

    const serverRows = convertFilesToSmartTableRows(selectedNotebook.files);

    // Merge with existing rows
    const byId = new Map(serverRows.map(r => [String(r.file_id), r]));
    const byName = new Map(
      serverRows.filter(r => r.filename).map(r => [r.filename.toLowerCase(), r])
    );

    const usedServerIds = new Set();

    const mergedRows = (nanoMetadataRows || []).map(row => {
      const rowId = String(row?.file_id || '');
      const rowName = String(row?.filename || row?.basic_info?.name || '').toLowerCase();

      const match = (rowId ? byId.get(rowId) : null) || (rowName ? byName.get(rowName) : null);
      // ✅ ENTERPRISE: Purge orphan nano-metadata rows that don't exist in the vault mapping.
      // This eliminates the "Leaked Files" mismatch between Nano-Table and Sources panel.
      if (!match) {
        // ✅ Keep local (pre-server) rows while an upload is in-flight.
        // Otherwise, the Nano table drops the row and the DNA micro-insight never appears.
        const hasLocalFile = !!row?.originalFile;
        const localStatus = String(row?.status || '').toLowerCase();
        const isTransient =
          hasLocalFile &&
          (localStatus === 'uploading' ||
            localStatus === 'queued' ||
            localStatus === 'processing' ||
            localStatus === 'analyzing' ||
            localStatus === '');
        return isTransient ? row : null;
      }

      usedServerIds.add(String(match.file_id));
      // ✅ Preserve locally extracted nano-metadata/DNA while the backend catches up.
      // Match (server truth) wins for status/progress, but local nanoMetadata can be richer.
      const merged = {
        ...row,
        ...match,
        originalFile: row.originalFile,
      };

      // Preserve nano-metadata payloads if backend didn't send them
      merged.nanoMetadata = match.nanoMetadata ?? row.nanoMetadata ?? null;
      merged.nano_metadata = match.nano_metadata ?? row.nano_metadata ?? null;

      // Preserve DNA / details blobs if missing
      merged.dna = { ...(row.dna || {}), ...(match.dna || {}) };
      merged.details = { ...(row.details || {}), ...(match.details || {}) };
      merged.basic_info = { ...(row.basic_info || {}), ...(match.basic_info || {}) };
      merged.quick_health = { ...(row.quick_health || {}), ...(match.quick_health || {}) };

      return merged;
    });

    for (const sr of serverRows) {
      if (!usedServerIds.has(String(sr.file_id))) {
        mergedRows.push(sr);
      }
    }

    // Deduplicate
    const dedup = new Map();
    for (const r of mergedRows.filter(Boolean)) {
      const id = String(r?.file_id || '');
      if (id && !dedup.has(id)) {
        dedup.set(id, r);
      }
    }

    const nextRows = Array.from(dedup.values());

    // Only update if changed
    const sig = rows =>
      (rows || []).map(r => `${r.file_id}:${r.status}:${r.processingProgress}`).join('|');
    if (setNanoMetadataRowsDirectly && sig(nextRows) !== sig(nanoMetadataRows)) {
      setNanoMetadataRowsDirectly(nextRows);
    }
  }, [
    selectedNotebook?.files,
    selectedNotebook?.id,
    convertFilesToSmartTableRows,
    setNanoMetadataRowsDirectly,
    nanoMetadataRows,
  ]);

  return { convertFilesToSmartTableRows, formatBytes };
}
